import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

import { ProfileAutomationsTabs } from '~/feature/agents';
import { useTranslate } from '~/shared/context/use-translate';
import { TabbedPanel } from '~/ui/core/tabs/Tabs';

import { JSA_PROFILE_TILE_TABS, type JsaProfileTileTab } from '../url-tile-payloads';

/** The role or level a profile tile shows, and the Position Architecture whose positions it lists, if one is given. */
export type JobProfileTileSubject = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly positionArchitectureId: string | undefined;
};

/**
 * What the profile tile cannot read from the Job Architecture: the tasks and positions of its role or level, and the
 * whole tile for a user who may not read the Job Architecture. The AI transformation feature provides them.
 */
export type JobProfileTilePanels = {
    readonly Positions: FunctionComponent<JobProfileTileSubject>;
    /** `fallback` is the profile's own task list, shown when no Position Architecture lists the tasks. */
    readonly Tasks: FunctionComponent<JobProfileTileSubject & { readonly fallback: ReactNode }>;
    readonly WithoutJobArchitecture: FunctionComponent<
        JobProfileTileSubject & { readonly initialTab: JsaProfileTileTab; readonly onClose: () => void }
    >;
};

const JobProfileTilePanelsContext = createContext<JobProfileTilePanels | null>(null);

export const JobProfileTilePanelsProvider: FunctionComponent<
    PropsWithChildren<{ readonly panels: JobProfileTilePanels }>
> = ({ children, panels }) => (
    <JobProfileTilePanelsContext.Provider value={panels}>{children}</JobProfileTilePanelsContext.Provider>
);

export const useJobProfileTilePanels = (): JobProfileTilePanels | null => useContext(JobProfileTilePanelsContext);

type JobProfileTileTabsSetup = {
    readonly initialTab: JsaProfileTileTab;
    readonly positionArchitectureId: string | undefined;
};

const JobProfileTileTabsSetupContext = createContext<JobProfileTileTabsSetup | null>(null);

/** Marks the profile content as a tile's, which shows its tabs, starting on `initialTab`. */
export const JobProfileTileTabsSetupProvider: FunctionComponent<PropsWithChildren<JobProfileTileTabsSetup>> = ({
    children,
    initialTab,
    positionArchitectureId,
}) => (
    <JobProfileTileTabsSetupContext.Provider value={{ initialTab, positionArchitectureId }}>
        {children}
    </JobProfileTileTabsSetupContext.Provider>
);

const isProfileTileTab = (value: string): value is JsaProfileTileTab =>
    JSA_PROFILE_TILE_TABS.some((tab) => tab === value);

type ShownTab = JsaProfileTileTab | 'automations';

/** The tab bar of a role or level tile: Overview, Tasks, Automations when `automations` is given, then Positions. */
export const JobProfileTileTabBar: FunctionComponent<{
    readonly automations: ReactNode | undefined;
    readonly initialTab: JsaProfileTileTab;
    readonly overview: ReactNode;
    readonly positions: ReactNode;
    readonly tasks: ReactNode;
}> = ({ automations, initialTab, overview, positions, tasks }) => {
    const translate = useTranslate();
    const [tab, setTab] = useState<ShownTab>(initialTab);
    const panels: Record<ShownTab, ReactNode> = { automations, overview, positions, tasks };
    const labels: Record<ShownTab, string> = {
        automations: translate('pages.job-architecture-v2.profile-tasks.agents.tab-automations'),
        overview: translate('pages.job-architecture-v2.profile-tile-tabs.overview'),
        positions: translate('pages.job-architecture-v2.profile-tile-tabs.positions'),
        tasks: translate('pages.job-architecture-v2.profile-tile-tabs.tasks'),
    };
    const shownTabs: readonly ShownTab[] =
        automations === undefined
            ? ['overview', 'tasks', 'positions']
            : ['overview', 'tasks', 'automations', 'positions'];
    const activeTab = shownTabs.includes(tab) ? tab : 'overview';

    return (
        <TabbedPanel
            panel={panels[activeTab]}
            panelClassName="min-w-0"
            tabs={shownTabs.map((value) => ({ label: labels[value], value }))}
            testId="job-profile-tile-tabs"
            value={activeTab}
            onValueChange={(value) => {
                if (isProfileTileTab(value) || value === 'automations') setTab(value);
            }}
        />
    );
};

/**
 * The profile body of a role or level. In a tile it sits under the Overview tab, next to the role's or level's Tasks
 * and Positions; while editing, and outside a tile, it shows the profile alone, with the Automations tab when shown.
 */
export const JobProfileTileTabs: FunctionComponent<{
    readonly architectureId: string;
    readonly automations: ReactNode;
    readonly isEditing: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly overview: ReactNode;
    readonly showAutomations: boolean;
    readonly tasksFallback: ReactNode;
}> = ({ architectureId, automations, isEditing, itemId, itemType, overview, showAutomations, tasksFallback }) => {
    const setup = useContext(JobProfileTileTabsSetupContext);
    const panels = useJobProfileTilePanels();

    if (setup === null || panels === null || isEditing) {
        return (
            <ProfileAutomationsTabs automations={automations} isEnabled={showAutomations}>
                {overview}
            </ProfileAutomationsTabs>
        );
    }

    const subject: JobProfileTileSubject = {
        architectureId,
        itemId,
        itemType,
        positionArchitectureId: setup.positionArchitectureId,
    };

    return (
        <JobProfileTileTabBar
            automations={showAutomations ? automations : undefined}
            initialTab={setup.initialTab}
            overview={overview}
            positions={<panels.Positions {...subject} />}
            tasks={<panels.Tasks {...subject} fallback={tasksFallback} />}
        />
    );
};
